import React from 'react';
import { Linkedin } from 'lucide-react';
import { Seo, breadcrumbSchema, webPageSchema } from '../components/Seo';
import PageHeader from '../components/PageHeader';
import Reveal from '../components/Reveal';

/**
 * Source of truth for the jury data. Order here == display order on the page.
 * Photos live in `/public/jury/<slug>.jpg` (referenced by the `photo` field).
 * `linkedin` is optional — when present, a small LinkedIn icon is rendered
 * next to the juror's name (opens in a new tab with `rel="noopener"`). Jurors
 * without a URL remain plain non-clickable text.
 * Add or reorder rows and the layout updates automatically.
 */
const JURY_CHAIRS = [
  { name: 'Avinash Kaul',              designation: 'Former CEO, Network18 (Broadcast & Publishing)',                                    photo: '/jury/avinash-kaul.jpg',              linkedin: 'https://in.linkedin.com/in/avikaul' },
  { name: 'Aroop Chatterjee',          designation: 'National Head — Brand & Marketing, Adani Cement',                                    photo: '/jury/aroop-chatterjee.jpg',          linkedin: 'https://in.linkedin.com/in/aroopchatterjee' },
  { name: 'Dipshika Ravi',             designation: 'National Creative Director, Schbang',                                                photo: '/jury/dipshika-ravi.jpg',             linkedin: 'https://in.linkedin.com/in/dipshikaravi' },
  { name: 'Shailja Saraswati Varghese', designation: 'Founder, Unstoppable Network · Creator-Host, Unstoppable Woman · Executive Coach', photo: '/jury/shailja-saraswati-varghese.jpg', linkedin: 'https://in.linkedin.com/in/shailja-saraswati' },
  { name: 'Manesh Swamy',              designation: 'Co-Founder & Chief Creative Officer, FIRSTAI Consultancy Services',                  photo: '/jury/manesh-swamy.jpg',              linkedin: 'https://in.linkedin.com/in/maneshswamy' },
  { name: 'Niraj Ruparel',             designation: 'Creative Technology Lead, WPP',                                                      photo: '/jury/niraj-ruparel.jpg',             linkedin: 'https://in.linkedin.com/in/ruparelniraj' },
];

const JURY_PANEL = [
  { name: 'Abhishek Ranjan',   designation: 'Digital Head, Catch Spices, DS Group',                          photo: '/jury/abhishek-ranjan.jpg',   linkedin: 'https://in.linkedin.com/in/onlineabhishek' },
  { name: 'Anuiya Yadav',      designation: 'Content Strategy Head, Network18',                              photo: '/jury/anuiya-yadav.jpg',      linkedin: 'https://www.linkedin.com/in/anuiya-yadav-2165a5228/' },
  { name: 'Charlie Cowdrey',   designation: 'CEO, JioBLAST',                                                 photo: '/jury/charlie-cowdrey.jpg',   linkedin: 'https://in.linkedin.com/in/charlie-cowdrey' },
  { name: 'Devanshi Shah',     designation: 'Head of Marketing, Beyond Snack',                               photo: '/jury/devanshi-shah.jpg',     linkedin: 'https://in.linkedin.com/in/devanshi-shah-a76248a4' },
  { name: 'Dhruv Sachdeva',    designation: 'Founder, Humour Me',                                            photo: '/jury/dhruv-sachdeva.jpg',    linkedin: 'https://in.linkedin.com/in/dhrvsachdeva' },
  { name: 'Indranil Das Blah', designation: 'Founder, AMP Sports & Entertainment',                           photo: '/jury/indranil-das-blah.jpg', linkedin: 'https://in.linkedin.com/in/indranil-das-blah-b749832' },
  { name: 'Jag Chima',         designation: 'Co-Founder, IPLIX Media',                                       photo: '/jury/jag-chima.jpg',         linkedin: 'https://in.linkedin.com/in/jagchima' },
  { name: 'Kunal Madan',       designation: 'CMO, Radico Khaitan',                                           photo: '/jury/kunal-madan.jpg',       linkedin: 'https://in.linkedin.com/in/kunal-madan-3a78674' },
  { name: 'Nikita Singh',      designation: 'Marketing, Brand & Digital Specialist, Fortis Healthcare',      photo: '/jury/nikita-singh.jpg',      linkedin: 'https://in.linkedin.com/in/nikita-singh-2703992a' },
  { name: 'Nisha Singhania',   designation: 'Co-Founder & Managing Partner, Infectious',                     photo: '/jury/nisha-singhania.jpg',   linkedin: 'https://in.linkedin.com/in/nisha-s-a441622' },
  { name: 'Raghuveer Singh',   designation: 'VP — Social Media & Content Marketing, India & Global, ZEE5',   photo: '/jury/raghuveer-singh.jpg',   linkedin: 'https://www.linkedin.com/in/raghuveersingh/' },
  { name: 'Roli Shrivastava',  designation: 'CMO, Hocco',                                                    photo: '/jury/roli-shrivastava.jpg',  linkedin: 'https://in.linkedin.com/in/rolishrivastava' },
  { name: 'Rupa Murudkar',     designation: 'VP — Marketing, Cholayil',                                      photo: '/jury/rupa-murudkar.jpg',     linkedin: 'https://in.linkedin.com/in/rupa-murudkar-b7268ba9' },
  { name: 'Shivam Porwal',     designation: 'Head of Digital Marketing, Bistro & Blinkit',                   photo: '/jury/shivam-porwal.jpg',     linkedin: 'https://in.linkedin.com/in/iamshivamporwal' },
  { name: 'Shradha Agarwal',   designation: 'Co-Founder & Global CEO, Grapes Worldwide',                     photo: '/jury/shradha-agarwal.jpg',   linkedin: 'https://in.linkedin.com/in/shradsagarwal' },
  { name: 'Sumedha Mahajan',   designation: 'Business Head, India, Ampverse DMI',                            photo: '/jury/sumedha-mahajan.jpg',   linkedin: 'https://in.linkedin.com/in/sumedha11' },
  { name: 'Titus Upputuru',    designation: 'Founder & Film-Maker, The Titus Upputuru Company',              photo: '/jury/titus-upputuru.jpg',    linkedin: 'https://in.linkedin.com/in/titus-upputuru-353a0027' },
  { name: 'Viraj Sheth',       designation: 'Co-Founder & CEO, Monk Entertainment',                          photo: '/jury/viraj-sheth.jpg',       linkedin: 'https://in.linkedin.com/in/virajsheth' },
  { name: 'Viren Noronha',     designation: 'Co-Founder & CEO, The New Thing',                               photo: '/jury/viren-noronha.jpg',     linkedin: 'https://in.linkedin.com/in/vsnoronha' },
  { name: 'Sandipan Aich',     designation: 'GM — Corporate Relations & PR, K.K. Birla Group of Companies',  photo: '/jury/sandipan-aich.jpg',     linkedin: 'https://www.linkedin.com/in/sandipan-aich-9076a0141/' },
];

// Reusable card — larger for chairs, compact for the panel. Photos are always
// masked to a circle so any residual page background from the source PDF
// crops disappears cleanly at the edges.
const JurorCard = ({ juror, size = 'panel' }) => {
  const isChair = size === 'chair';
  const dim = isChair
    ? 'w-[168px] h-[168px] md:w-[192px] md:h-[192px]'
    : 'w-[128px] h-[128px] md:w-[140px] md:h-[140px]';
  const nameCls = isChair
    ? 'font-serif text-white text-lg md:text-xl leading-tight'
    : 'font-serif text-white text-base md:text-[17px] leading-tight';
  const desigCls = isChair
    ? 'font-sans text-white/55 text-[12.5px] md:text-[13px] leading-relaxed mt-2 max-w-[280px]'
    : 'font-sans text-white/55 text-[11.5px] md:text-[12px] leading-relaxed mt-2 max-w-[240px]';

  return (
    <div className="flex flex-col items-center text-center" data-testid={`juror-card-${juror.name.replace(/\s+/g, '-').toLowerCase()}`}>
      <div className="relative">
        <div
          className={`${dim} rounded-full overflow-hidden border border-[#C9A56C]/25 group-hover:border-[#C9A56C]/60 transition-colors bg-[#3A2418]/50`}
          aria-hidden={juror.photo ? undefined : true}
        >
          {juror.photo ? (
            <img
              src={juror.photo}
              alt={juror.name}
              loading="lazy"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[#C9A56C]/70 font-serif text-2xl">
              {juror.name.split(' ').map((n) => n[0]).slice(0, 2).join('')}
            </div>
          )}
        </div>
        {/* LinkedIn badge — sits on the photo circle at bottom-right like a
            verified/profile chip. Walnut disc + gold hairline to match the
            existing photo ring, so it feels editorial rather than a bright
            LinkedIn stamp. Fully absent when the juror has no URL, keeping
            the original look byte-identical for anyone missing a profile. */}
        {juror.linkedin && (
          <a
            href={juror.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${juror.name} on LinkedIn`}
            title={`${juror.name} on LinkedIn`}
            data-testid={`juror-linkedin-${juror.name.replace(/\s+/g, '-').toLowerCase()}`}
            className={`absolute ${isChair ? 'w-8 h-8 bottom-1 right-1' : 'w-7 h-7 bottom-0.5 right-0.5'}
              rounded-full flex items-center justify-center
              bg-[#3A2418] border border-[#C9A56C]/45 text-[#E9D8B4]
              shadow-[0_2px_8px_rgba(0,0,0,0.35)]
              hover:bg-[#4A2E1E] hover:border-[#C9A56C] hover:text-white transition-colors`}
          >
            <Linkedin size={isChair ? 14 : 12} strokeWidth={2} fill="currentColor" />
          </a>
        )}
      </div>
      <div className="mt-5">
        <div className={nameCls}>{juror.name}</div>
        <div className={desigCls}>{juror.designation}</div>
      </div>
    </div>
  );
};

const Jury = () => (
  <div data-testid="page-jury">
    <Seo
      title="Jury · AdAura Awards 2026 · India's Marketing Leaders"
      description="Meet the AdAura Awards 2026 jury — CMOs, agency heads, creative leaders and marketing veterans anchoring India's most prestigious marketing awards."
      path="/jury"
      jsonLd={[
        webPageSchema({ path: '/jury', name: 'Jury', description: "Meet the AdAura Awards 2026 jury — CMOs, agency heads, creative leaders and marketing veterans." }),
        breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Jury', path: '/jury' }]),
      ]}
    />
    <PageHeader
      title="The Jury"
      italicWords={['Jury']}
    />

    {/* JURY CHAIRS */}
    <section className="pb-16 md:pb-24" data-testid="jury-chairs-section">
      <div className="content-container">
        <Reveal>
          <div className="flex items-center justify-center gap-4 mb-14 md:mb-16">
            <span aria-hidden className="h-px w-10 bg-[#C9A56C]/60" />
            <div className="eyebrow text-center">Jury Chair</div>
            <span aria-hidden className="h-px w-10 bg-[#C9A56C]/60" />
          </div>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14 md:gap-y-16 max-w-5xl mx-auto">
          {JURY_CHAIRS.map((c, i) => (
            <Reveal key={c.name} delay={80 * i}>
              <JurorCard juror={c} size="chair" />
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* JURY PANEL */}
    <section className="pb-24 md:pb-32" data-testid="jury-panel-section">
      <div className="content-container">
        <Reveal>
          <div className="flex items-center justify-center gap-4 mb-14 md:mb-16">
            <span aria-hidden className="h-px w-10 bg-[#C9A56C]/60" />
            <div className="eyebrow text-center">Jury Panel</div>
            <span aria-hidden className="h-px w-10 bg-[#C9A56C]/60" />
          </div>
        </Reveal>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-x-6 gap-y-12 md:gap-y-14 max-w-6xl mx-auto">
          {JURY_PANEL.map((j, i) => (
            <Reveal key={j.name} delay={40 * i}>
              <JurorCard juror={j} size="panel" />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  </div>
);

export default Jury;
